// DESCRIPTION: Filter bar for the calendar — multi-select category and tag dropdowns fetched from the WP REST API.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiFetch from '@wordpress/api-fetch';
import { __ } from '@wordpress/i18n';

const strings = ( window.nemtlyCalendar || {} ).strings || {};

/**
 * A dropdown that renders a list of checkboxes for multi-selection.
 *
 * @param {Object}   props
 * @param {string}   props.label    Placeholder text when nothing is selected.
 * @param {Array}    props.items    Array of { id, name } objects to choose from.
 * @param {number[]} props.selected Currently selected IDs.
 * @param {Function} props.onChange Called with the updated array of IDs.
 */
function MultiSelectDropdown( { label, items, selected, onChange } ) {
	const [ open, setOpen ] = useState( false );
	const ref = useRef( null );

	// Close when clicking outside.
	useEffect( () => {
		const handleClick = ( e ) => {
			if ( ref.current && ! ref.current.contains( e.target ) ) {
				setOpen( false );
			}
		};
		document.addEventListener( 'mousedown', handleClick );
		return () => document.removeEventListener( 'mousedown', handleClick );
	}, [] );

	// Close on Escape.
	useEffect( () => {
		const handleKey = ( e ) => {
			if ( e.key === 'Escape' ) {
				setOpen( false );
			}
		};
		document.addEventListener( 'keydown', handleKey );
		return () => document.removeEventListener( 'keydown', handleKey );
	}, [] );

	const toggle = useCallback(
		( id ) => {
			const next = selected.includes( id )
				? selected.filter( ( s ) => s !== id )
				: [ ...selected, id ];
			onChange( next );
		},
		[ selected, onChange ]
	);

	const selectedNames = items
		.filter( ( item ) => selected.includes( item.id ) )
		.map( ( item ) => item.name );

	const buttonText =
		selectedNames.length > 0 ? selectedNames.join( ', ' ) : label;

	return (
		<div className="nb-calendar-multiselect" ref={ ref }>
			<button
				className="nb-calendar-filter-select nb-calendar-multiselect__trigger"
				onClick={ () => setOpen( ! open ) }
				aria-expanded={ open }
				aria-haspopup="listbox"
				type="button"
			>
				<span className="nb-calendar-multiselect__text">
					{ buttonText }
				</span>
				<span
					className="nb-calendar-multiselect__arrow"
					aria-hidden="true"
				>
					{ open ? '▲' : '▼' }
				</span>
			</button>

			{ open && (
				<div
					className="nb-calendar-multiselect__dropdown"
					role="listbox"
					aria-multiselectable="true"
				>
					{ items.map( ( item ) => {
						const checked = selected.includes( item.id );
						return (
							<label
								key={ item.id }
								className={ `nb-calendar-multiselect__option${
									checked
										? ' nb-calendar-multiselect__option--checked'
										: ''
								}` }
								role="option"
								aria-selected={ checked }
							>
								<input
									type="checkbox"
									className="nb-calendar-multiselect__checkbox"
									checked={ checked }
									onChange={ () => toggle( item.id ) }
								/>
								<span>{ item.name }</span>
							</label>
						);
					} ) }
				</div>
			) }
		</div>
	);
}

/**
 * CalendarFilters
 *
 * @param {Object}   props
 * @param {Object}   props.filters         Current active filters { categories: [], tags: [] }
 * @param {Function} props.onFiltersChange Callback when filters change.
 */
export default function CalendarFilters( { filters, onFiltersChange } ) {
	const [ categories, setCategories ] = useState( [] );
	const [ tags, setTags ] = useState( [] );

	useEffect( () => {
		apiFetch( {
			path: '/wp/v2/nemtly_event_category?per_page=100&orderby=name&order=asc',
		} )
			.then( ( data ) => setCategories( data ) )
			.catch( () => {} );

		apiFetch( {
			path: '/wp/v2/nemtly_event_tag?per_page=100&orderby=name&order=asc',
		} )
			.then( ( data ) => setTags( data ) )
			.catch( () => {} );
	}, [] );

	const hasActiveFilter =
		filters.categories.length > 0 || filters.tags.length > 0;

	return (
		<div className="nb-calendar-filters">
			{ categories.length > 0 && (
				<MultiSelectDropdown
					label={
						strings.allCategories ||
						__( 'All Categories', 'nemtly-booking' )
					}
					items={ categories }
					selected={ filters.categories }
					onChange={ ( ids ) =>
						onFiltersChange( { ...filters, categories: ids } )
					}
				/>
			) }

			{ tags.length > 0 && (
				<MultiSelectDropdown
					label={
						strings.allTags || __( 'All Tags', 'nemtly-booking' )
					}
					items={ tags }
					selected={ filters.tags }
					onChange={ ( ids ) =>
						onFiltersChange( { ...filters, tags: ids } )
					}
				/>
			) }

			{ hasActiveFilter && (
				<button
					className="nb-calendar-filter-clear"
					onClick={ () =>
						onFiltersChange( { categories: [], tags: [] } )
					}
					type="button"
				>
					{ strings.clearFilters ||
						__( 'Clear filters', 'nemtly-booking' ) }
				</button>
			) }
		</div>
	);
}
